import { DomMutation } from 'shared/dom-mutation-core';
import {
    DriverscamVideoMinimumInfo,
    Hotspot,
MinimumSlideInformation,
    QuestionIdentifier,
    SlideTypeCategory,
} from '@pcp/core/interfaces';
import {
    StopLessonCommand,
} from './lesson-commands';
import { ClassroomCommand, CommandType } from './streaming';

export const isSlideCommand = (command: ClassroomCommand): command is SlideCommand => 'layer' in command;

export const isClassroomCommand = (command: ClassroomCommand): command is ClassroomCommand =>
    'commandType' in command && 'commandParam' in command;

export const isLayerAwareSlideCommand = (command: any): command is LayerAwareSlideCommand => 'layer' in command;

export const isNavigateToPreviewSlideCommand = (command: ClassroomCommand): command is NavigateToPreviewSlideCommand =>
    command.commandType === CommandType.NAVIGATE_TO_PREVIEW_SLIDE;

export const isNavigateToPreviewDriverscamAssignmentCommand = (
    command: ClassroomCommand,
): command is NavigateToPreviewDriverscamAssignmentCommand =>
    command.commandType === CommandType.NAVIGATE_TO_PREVIEW_DRIVERSCAM_ASSIGNMENT;

export const isShowDifficultQuestionElementCommand = (
    command: ClassroomCommand,
): command is ShowDifficultQuestionElementCommand =>
    command.commandType === CommandType.SHOW_DIFFICULT_QUESTION_ELEMENT;

export const isUpdateSlideContentCommand = (command: ClassroomCommand): command is UpdateSlideContentCommand =>
    command.commandType === CommandType.UPDATE_SLIDE;

export const isActivateHotspotCommand = (command: ClassroomCommand): command is SendActivateHotspotCommand =>
    command.commandType === CommandType.ACTIVATE_HOTSPOT;

export const isUndoCommand = (command: ClassroomCommand): command is SendUndoCommand =>
    command.commandType === CommandType.UNDO;

export const isRedoCommand = (command: ClassroomCommand): command is SendRedoCommand =>
    command.commandType === CommandType.REDO;

export const isNavigateToParentCommand = (command: ClassroomCommand): command is SendNavigateToParentCommand =>
    command.commandType === CommandType.NAVIGATE_TO_PARENT;

export const isStopLessonCommand = (command: ClassroomCommand): command is StopLessonCommand =>
    command.commandType === CommandType.STOP_LESSON;


export const isMutateCanvasElementCommand = (command: ClassroomCommand): command is UpdateSlideContentCommand =>
    isUpdateSlideContentCommand(command) && command.forElementType === 'canvas';

export const isMutateInteractiveElementCommand = (command: ClassroomCommand): command is UpdateSlideContentCommand =>
    isUpdateSlideContentCommand(command) && command.forElementType === 'interactive';

export const isMutateRubberElementCommand = (command: ClassroomCommand): command is UpdateRubberElementCommand =>
    command.commandType === CommandType.UPDATE_CANVAS;
// && 'forElementType' in command
// && command.forElementType === 'rubber';

/**
 * Layer of the related slide (positive number).
 * Lower layers are hidden by higher layer numbers.
 *
 * Primary slide   = layer 1
 * Secondary slide = layer 2
 */
export interface LayerAwareSlideCommand {
    layer: 1 | 2 | 3;
}

/**
 * Slide command including a GUID of the slide the command is related to.
 * The GUID is uniquie for each slide _and_ for each display of the slide.
 *
 * E.g. if slide ID 2233 is shown twice, it will have two different GUIDs.
 */
export interface GuidAwareSlideCommand {
    guid: string;
}

export interface SlideCommand extends ClassroomCommand, LayerAwareSlideCommand, GuidAwareSlideCommand {}

/**
 * Update content of the current slide.
 */
export interface UpdateSlideContentCommand extends SlideCommand {
    commandType: CommandType.UPDATE_SLIDE;
    mutations: DomMutation[];
    forSlideId: number;
    forElementType: SlideTypeCategory;
}

/**
 * Update content of the current slide's canvas (eraser image).
 * TODO: rename to `UpdateRubberImageContentCommand`
 */
export interface UpdateRubberElementCommand extends SlideCommand {
    commandType: CommandType.UPDATE_CANVAS;
    changes: RubberImageUpdate;
    forSlideId: number;
}

export interface RubberImageUpdate {
    type: 'eraser';
    action: 'drawPoint' | 'move';
    x: number;
    y: number;
}

export interface SendActivateHotspotCommand extends ClassroomCommand {
    commandType: CommandType.ACTIVATE_HOTSPOT;
    hotspot: Hotspot;
    forSlideId: number;
}

export interface SendUndoCommand extends ClassroomCommand {
    commandType: CommandType.UNDO;
}

export interface SendRedoCommand extends ClassroomCommand {
    commandType: CommandType.REDO;
}

export interface SendNavigateToParentCommand extends ClassroomCommand {
    commandType: CommandType.NAVIGATE_TO_PARENT;
}

export interface NavigateToPreviewSlideCommand extends ClassroomCommand {
    commandType: CommandType.NAVIGATE_TO_PREVIEW_SLIDE;
    commandParam: '';
    slide: MinimumSlideInformation;
}

export interface NavigateToPreviewDriverscamAssignmentCommand extends ClassroomCommand {
    commandType: CommandType.NAVIGATE_TO_PREVIEW_DRIVERSCAM_ASSIGNMENT;
    commandParam: '';
    driverscam: DriverscamVideoMinimumInfo;
}

export interface ShowDifficultQuestionElementCommand extends ClassroomCommand {
    commandType: CommandType.SHOW_DIFFICULT_QUESTION_ELEMENT;
    commandParam: '';
    difficultQuestionNumbers: QuestionIdentifier[];
}

